<template>
  <v-app class="bgImage">
    <m-errorbox ref="errorBox"></m-errorbox>
    <navbar>
      <v-toolbar-title class="text-uppercase secondary--text text--darken-3">
        <span class="font-weight-light">All</span>
        <span class="font-weight-medium">Devices</span>
      </v-toolbar-title>
      <v-card
        v-if="collection.alerted_devices > 0"
        outlined
        class="py-1 px-4 mx-4"
        color="error"
      >
        <!-- Modify to show only the number of devices with alerts -->
        <span class="white--text"
          >{{ collection.alerted_devices }} Device Alerts!</span
        >
      </v-card>
      <v-spacer></v-spacer>
    </navbar>
    <v-content class="secondary--text text--darken-2">
      <v-container fluid>
        <v-card>
          <m-errorbox ref="errorBox"></m-errorbox>
          <v-card-text>
            <form @submit.prevent="onSearchClick">
              <v-row align="end" justify="start">
                <v-col md="6" lg="3">
                  <v-text-field
                    v-model="search"
                    label="Text Search"
                    single-line
                    hide-details
                    clearable
                    prepend-icon="mdi-text-box-search"
                    @click:clear="onSearchClear"
                  ></v-text-field>
                </v-col>
                <v-col>
                  <v-btn outlined color="primary" type="submit">
                    Search
                    <v-icon right>mdi-magnify</v-icon>
                  </v-btn>
                </v-col>
              </v-row>
              <v-row align="end" justify="start">
                <v-col md="6" lg="3">
                  <h-autocomplete
                    :model="addressModel"
                    label="Address Search"
                    @update:address_search="setCoords"
                    prepend-icon="mdi-map-search"
                  ></h-autocomplete>
                </v-col>
              </v-row>

              <v-row>
                <v-col md="6" lg="3">
                  <v-checkbox
                    v-model="showAlertedDevices"
                    class="pa-0 ma-0"
                    color="primary"
                    @change="handleShowAlertedDevicesChange"
                  >
                    <template v-slot:label>
                      <label class="primary--text"
                        >Show Alerted Devices Only</label
                      >
                    </template>
                  </v-checkbox>
                </v-col>
              </v-row>
            </form>
          </v-card-text>

          <v-card-text>
            <v-data-table
              :headers="collection.meta.headers"
              :items="collection.data"
              :loading="collection.loading"
              :options="dataTableOptions"
              :footer-props="{ itemsPerPageOptions: [10, 15, 30, 50, 100] }"
              @click:row="onRowClicked"
              show-expand
              single-expand
              :expanded="expandedRows"
              hide-default-footer
            >
              <template v-slot:item="{ item, isExpanded, headers }">
                <tr @click="onRowClicked(item)">
                  <td
                    v-for="header in headers"
                    :key="header.value"
                    :style="{
                      'border-bottom-style': isExpanded ? 'none' : undefined
                    }"
                  >
                    <template v-if="header.value == 'alerted'">
                      <v-icon
                        v-if="item['silenced'] || item['alerted']"
                        :color="
                          item['silenced']
                            ? 'red lighten-4'
                            : item['alerted']
                            ? 'error'
                            : ''
                        "
                        start
                        >mdi-circle</v-icon
                      >
                    </template>
                    <template v-else>{{ item[header.value] }}</template>
                  </td>
                </tr>
              </template>

              <template v-slot:expanded-item="{ headers, item }">
                <tr @click="onRowClicked(item)">
                  <td :colspan="headers.length">
                    <h4>Comments:</h4>
                    <p>{{ item.comments || "(No Comments)" }}</p>
                    <v-btn
                      color="primary"
                      class="mb-2"
                      @click="$router.push('/log/' + item.id)"
                      >Open Chart
                      <v-icon right>mdi-chart-timeline-variant</v-icon></v-btn
                    >
                    <v-btn
                      class="mb-2 ml-2 secondary--text text--darken-3"
                      @click="$router.push('/deviceinfo/' + item.id)"
                      >Open Info Page<v-icon right
                        >mdi-information</v-icon
                      ></v-btn
                    >

                    <m-form
                      :query="apiEndpoints.addDevice"
                      :params="[item.id]"
                      :modelSrc="item"
                      @success="onAddSuccess"
                      style=" display:inline!important;"
                    >
                      <v-btn
                        @click="onAddClick"
                        class="mb-2 ml-2 secondary--text text--darken-3"
                        type="submit"
                        >Add to My Devices<v-icon right
                          >mdi-plus-circle</v-icon
                        ></v-btn
                      >
                    </m-form>
                    <v-btn
                      color="secondary darken-1 white--text"
                      class="mb-2"
                      @click="goToDownloadReadingsPage(item.id)"
                      >Download All Readings
                      <v-icon right>mdi-download</v-icon></v-btn
                    >
                    <v-btn
                      color="secondary darken-1 white--text"
                      class="mb-2"
                      @click="goToDownloadReadingsPage(item.id, 'trend')"
                      >Download Trend Readings
                      <v-icon right>mdi-download-lock</v-icon></v-btn
                    >
                    <!-- Show the silence button when the device is alerted -->
                    <v-checkbox
                      v-show="item.alerted"
                      v-model="item.silenced"
                      class="pa-0 mb-2"
                      color="primary"
                      @click.stop="clickSilenceDevice(item.id)"
                    >
                      <template v-slot:label>
                        <label class="primary--text">Silence this device</label>
                      </template>
                    </v-checkbox>
                  </td>
                </tr>
              </template>
            </v-data-table>
            <my-pagination
              :meta="collection.meta"
              @request-page="onPaginationRequest"
            />
          </v-card-text>
        </v-card>
      </v-container>
    </v-content>
  </v-app>
</template>

<script>
import apiRequest from "../mixins/api-request";
import apiEndpoints from "../mixins/api-endpoints";
import formatDate from "../mixins/format-date";
import MyPagination from "../components/MyPagination.vue";

export default {
  components: {
    MyPagination
  },
  mixins: [apiRequest, apiEndpoints, formatDate],
  data() {
    return {
      collection: {
        data: [],
        meta: {
          current_page: this.$route.query.page || "1",
          last_page: 1,
          headers: [],
          per_page: this.$route.query.per_page || "10",
          collection_name: "Log",
          total: 0
        },
        loading: true,
        alerted_devices: 0
      },
      addressModel: {
        values: {}
      },
      search: "",
      showAlertedDevices: Boolean(this.$route.query.alert_only),
      expandedRows: [],
      validationRules: {
        search_term: model => [
          v =>
            !v ||
            v.length <= 255 ||
            "Search term must be less than 256 characters"
        ],
        billing_email: model => [
          v => !!v || "Billing E-mail is required",
          v => /.+@.+\..+/.test(v) || "Billing E-mail must be valid"
        ],
        user_name: model => [
          v => !!v || "Name is required",
          v => (v && v.length >= 5) || "Name must be at least 5 characters"
        ],
        email: model => [
          v => !!v || "User E-mail is required",
          v => /.+@.+\..+/.test(v) || "User E-mail must be valid"
        ],
        group_name: model => [
          v => !!v || "Group name is required",
          v =>
            (v && v.length >= 5) || "Group name must be at least 5 characters"
        ],
        description: model => [
          v =>
            !v ||
            v.length <= 256 ||
            "Group description must be less than 256 characters"
        ]
      }
    };
  },
  watch: {
    "$route.query": {
      immediate: true,
      deep: true,
      handler(value) {
        this.performRequest();
      }
    }
  },
  computed: {
    dataTableOptions() {
      return {
        page: Number(this.collection.meta.current_page),
        itemsPerPage: Number(this.collection.meta.per_page)
      };
    }
  },
  methods: {
    showError(message) {
      this.$refs.errorBox.showError(message);
    },
    setCoords(evt) {
      //console.log(evt);
    },
    onSearchClick() {
      this.routeToPageNumber();
    },
    onSearchClear() {
      this.search = "";
      this.routeToPageNumber();
    },
    onSearchSuccess(results) {
      console.log(results);
    },
    onAddSuccess(body) {
      console.log(body);
      this.$router.replace("my_devices");
    },
    onAddClick(e) {
      //stop the click event from closing the expanded row.
      e.stopPropagation();
    },
    onRowClicked(row) {
      if (this.expandedRows[0] == row) {
        this.expandedRows = [];
      } else {
        this.expandedRows = [row];
      }
    },
    prepareModel(value) {
      value.last_contact = new Date(value.last_contact * 1000);
      value.last_contact =
        value.last_contact.getFullYear() < 2001
          ? "Never been online"
          : this.fullDateTime(value.last_contact);
      value.online = value.online ? "ONLINE" : "OFFLINE";
      value.device_name = value.device_name || "Not Set.";
      value.alerted = Boolean(value.alerted); // If alerted is 1 then alerted is true
      value.silenced = Boolean(value.silenced); // If silenced is 1 then silenced is true
    },
    routeToPageNumber(pageNumber, itemsPerPage) {
      var queryParams = {
        per_page: itemsPerPage || this.$route.query.per_page || 10,
        page: pageNumber || 1
      };

      if (this.search) {
        queryParams.search_term = this.search;
      }

      if (
        this.addressModel.values.latitude &&
        this.addressModel.values.longitude
      ) {
        queryParams.latitude = this.addressModel.values.latitude;
        queryParams.longitude = this.addressModel.values.longitude;
      }

      if (this.showAlertedDevices) {
        queryParams.alert_only = 1;
      }

      this.$router.replace({ query: queryParams }).catch(err => {
        console.error(err);
      });
    },
    performRequest() {
      this.collection.loading = true;
      this.expandedRows = [];

      var queryParams = Object.assign({}, this.$route.query);

      let endpoint = this.apiEndpoints.devices;

      this.apiRequest(endpoint, null, queryParams)
        .then(r => {
          if (this.statusOk(r.status)) {
            Vue.set(this, "collection", r.body);
            this.collection.data.forEach(value => {
              this.prepareModel(value);
            });
          } else {
            this.showError("Error fetching data from server");
            console.error(
              "Error fetching data from server: ",
              r.body.message ||
                `No Error Message Returned! (Status ${r.status})`
            );
          }
        })
        .catch(err => console.error(err));
    },
    clickSilenceDevice(deviceId) {
      const device = this.collection.data.find(item => item.id === deviceId);
      device.silenced = !device.silenced;
      this.apiRequest(this.apiEndpoints.updateDevice, [device.id], {
        silenced: Number(device.silenced)
      }).then(r => {
        if (this.statusOk(r.status)) {
          this.collection.alerted_devices = r.body.alerted_devices;
        } else {
          this.showError("Error silencing this device");
          device.silenced = !device.silenced; // revert the change
        }
      });
    },
    handleShowAlertedDevicesChange() {
      // Filter the devices based on the showAlertedDevices value
      this.routeToPageNumber();
    },
    goToDownloadReadingsPage(deviceId, typeOfDownload) {
      this.$router.push({
        name: "download_readings",
        params: { deviceId },
        query: {
          type: typeOfDownload
        }
      });
    },
    onPaginationRequest({ page, itemsPerPage }) {
      this.routeToPageNumber(page, itemsPerPage);
    }
  }
};
</script>
